import React from 'react';
import styled from 'styled-components';
import Section from './Section';
import Container from './Container';
import Button from './Button';
import { Link } from 'react-router-dom';

// Use a background image from Unsplash relevant to community support
const backgroundImage = 'https://images.unsplash.com/photo-1593113630400-ea4288922497?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80';

const GoalsSection = styled(Section)`
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url(${backgroundImage});
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    opacity: 0.15;
    z-index: 0;
  }
`;

const Content = styled(Container)`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 900px;
`;

const Title = styled.h2`
  margin-bottom: ${props => props.theme.spacing.lg};
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -${props => props.theme.spacing.sm};
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background-color: ${props => props.theme.colors.primary};
  }
`;

const Description = styled.p`
  font-size: ${props => props.theme.fontSizes.lg};
  line-height: 1.7;
  margin-bottom: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes.md};
  }
`;

const QuoteContainer = styled.div`
  margin: ${props => props.theme.spacing.xl} 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Quote = styled.blockquote`
  position: relative;
  font-size: ${props => props.theme.fontSizes.xl};
  font-style: italic;
  color: ${props => props.theme.colors.primary};
  max-width: 800px;
  text-align: center;
  margin: ${props => props.theme.spacing.md} 0;
  
  &:before,
  &:after {
    content: '"';
    font-family: Georgia, serif;
    font-size: 5rem;
    color: ${props => props.theme.colors.primary};
    opacity: 0.3;
    position: absolute;
  }
  
  &:before {
    top: -2rem;
    left: -1rem;
  }
  
  &:after {
    content: '"';
    bottom: -4rem;
    right: -1rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes.lg};
    
    &:before {
      top: -1.5rem;
      left: -0.5rem;
      font-size: 4rem;
    }
    
    &:after {
      bottom: -3rem;
      right: -0.5rem;
      font-size: 4rem;
    }
  }
`;

const CallToAction = styled.div`
  margin-top: ${props => props.theme.spacing.xxl};
  padding: ${props => props.theme.spacing.xl};
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: ${props => props.theme.shadows.md};
`;

const CTATitle = styled.h3`
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.secondary};
`;

const CTADescription = styled.p`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Goals: React.FC = () => {
  return (
    <GoalsSection id="goals" backgroundColor="light">
      <Content>
        <Title>Our Goal</Title>
        <Description>
          At the heart of everything we do is the belief that a little support can make a big difference.
          Our commitment to helping our community grew stronger during the pandemic, as we focused on
          extending a hand to families in need.
        </Description>

        <QuoteContainer>
          <Quote>
            We aim to build sustainable solutions that empower families not just for today,
            but for the long term, helping them regain stability and independence.
          </Quote>
        </QuoteContainer>

        <Description>
          Through every initiative, we strive to reflect the deep conviction behind our mission—to bring
          hope and create lasting change for those who need it most.
        </Description>

        <CallToAction>
          <CTATitle>Make a Difference</CTATitle>
          <CTADescription>
            Inspire and build a community of supporters who help individuals and families near you.
            At Project Assist, wellbeing is at the forefront of what we're working together towards.
            Our programs and activities are designed to be a catalyst that helps community members
            reach their goals and fulfill their potential to become self-reliant.
          </CTADescription>
          <Button
            variant="accent"
            size="large"
            as={Link}
            to="/donate"
          >
            Support Our Mission
          </Button>
        </CallToAction>
      </Content>
    </GoalsSection>
  );
};

export default Goals; 